import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Info, Settings } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

export const FormLabel = ({
  className,
  children,
  required,
  info,
  navigationTo,
  htmlFor,
}: {
  className?: string
  children?: string
  required?: boolean
  info?: ReactNode
  navigationTo?: string
  htmlFor?: string
}) => {
  return (
    <Label
      className={cn(`flex items-center relative pb-1 text-[15px] gap-0`, className)}
      data-html-for={htmlFor}
      htmlFor={htmlFor}
    >
      {children}
      <small className={`${required ? 'inline-flex' : 'hidden'} text-red-600`}>&nbsp;*</small>
      {navigationTo && (
        <Link
          to={navigationTo}
          onClick={(e) => {
            const shouldLeavePage = window.confirm(
              'Are you sure you want to leave this page? Unsaved data will be lost.'
            )
            if (!shouldLeavePage) e.preventDefault()
          }}
        >
          <Settings className='w-[12px] ml-2 h-[12px] ' />
        </Link>
      )}
      {info && (
        <HoverCard openDelay={200}>
          <HoverCardTrigger className='cursor-pointer'>
            <Info className='w-[12px] h-[12px] ml-2' />
          </HoverCardTrigger>
          <HoverCardContent className={cn('text-xs p-3 break-words max-w-md w-max z-[10050]')}>{info}</HoverCardContent>
        </HoverCard>
      )}
    </Label>
  )
}

export const FormError = ({ error }: { error: string | undefined }) => {
  return error ? (
    <div className='relative flex items-center gap-1 mt-1 animate-fade-in-error'>
      <svg
        className='w-3 h-3 text-red-400 shrink-0 animate-shake'
        fill='none'
        stroke='currentColor'
        strokeWidth={2}
        viewBox='0 0 24 24'
        aria-hidden='true'
      >
        <circle cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='2' fill='none' />
        <line x1='12' y1='8' x2='12' y2='13' stroke='currentColor' strokeWidth='2' />
        <circle cx='12' cy='16' r='1' fill='currentColor' />
      </svg>
      <span className='text-red-500 text-xs '>{error}</span>
      <style>
        {`
          @keyframes fadeInError {
            from { opacity: 0; transform: translateY(8px);}
            to { opacity: 1; transform: translateY(0);}
          }
          .animate-fade-in-error {
            animation: fadeInError 0.4s cubic-bezier(.4,0,.2,1);
          }
          @keyframes shake {
            0% { transform: translateX(0); }
            20% { transform: translateX(-2px); }
            40% { transform: translateX(2px); }
            60% { transform: translateX(-2px); }
            80% { transform: translateX(2px); }
            100% { transform: translateX(0); }
          }
          .animate-shake {
            animation: shake 0.4s;
          }
        `}
      </style>
    </div>
  ) : null
}
